import {
  Avatar,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
  ClickAwayListener,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import { enqueueSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import {
  getPublicSharingLink,
  hireCandidate,
  rejectCandidate,
  shortlistCandidates,
} from 'src/services/candidate.service';
import { getOrganizationId } from 'src/services/user.service';
import SvgColor from 'src/components/svg-color';
import PropTypes from 'prop-types';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { BASE_URL } from 'src/config-global';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';
import { useResponsive } from 'src/hooks/use-responsive';
import {
  CANDIDATE_BEST_MATCHING,
  CANDIDATE_CLOSE_TO_MATCH,
  CANDIDATE_MATCHING,
  ELIGIBLE_FOR_INTERVIEW,
  NOT_ELIGIBLE_FOR_INTERVIEW,
} from '../job-candidate/ai-recommendation-status-constants';
import {
  AI_INTERVIEW_CREATED,
  AI_INTERVIEW_EVALUATED,
  AI_INTERVIEW_IN_PROGRESS,
  AI_INTERVIEW_LINK_SENT,
  ANALYZING,
  CANDIDATE_HIRED,
  CANDIDATE_REJECTED,
  IN_PERSON_INTERVIEW_SCHEDULED,
  SCREENING_COMPLETED,
  SCREENING_DISQUALIFIED,
  SCREENING_ERROR,
  SCREENING_QUALIFIED,
} from '../job-candidate/job-candidate-status-constants';
import {
  AI_INTERVIEW,
  APPLIED,
  CODING_INTERVIEW_SUBMITTED,
  HIRED,
  INPERSON_INTERVIEW,
  NOT_SHORTLISTED,
  REJECTED,
  SHORTLISTED,
  SOURCED,
} from '../job-candidate/screening-status-constants';
import { InPersonState } from './constants/constants-nav';
import TrackingTimeline from './timeline';
import { formatFullDateString } from './utility';
import { CandidateProfileContext } from './context/candidate-profile-context';

const useStyles = makeStyles(theme => ({
  mainDiv: {
    height: '100%',
    gap: '1.2rem',
  },
  component: {
    padding: '0rem 1rem',
  },
  airecommendationDiv: {
    flexDirection: 'row',
    padding: '0.5rem',
    background: theme.palette.background.primaryAccent2,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 7,
    gap: 5,
    position: 'absolute',
    right: '12px',
    top: '12px',
  },
  componentSocialMain: {
    width: '100%',
    gap: '0.8rem',
    paddingTop: '40px',
  },
  currentJob: {
    color: theme.palette.content.primaryBlack3,
    textAlign: 'center',
    margin: 0,
    lineHeight: 'normal',
  },
  socialIconsDiv: {
    gap: '12px',
    justifyContent: 'center',
  },
  iconPadDiv: {
    background: theme.palette.background.primaryGrey1,
    color: theme.palette.content.primaryBlack1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    width: '40px',
    height: '40px',
  },
  button: {
    width: '100%',
  },
  errButton: {
    width: '100%',
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryRed2,
    },
  },
  candidateDetailsDiv: {
    gap: '1rem',
  },
  candidateDetailsAndTrackingText: {
    fontWeight: 700,
    fontSize: 18,
    margin: 0,
  },
  detailsContentDiv: {
    gap: '1rem',
  },
  detailsContent: {
    justifyContent: 'space-between',
  },
}));

// if stage = NULL, then APPLIED
const JOB_CANDIDATE_STAGE_UI_MAPPING = {
  [SOURCED]: 'Applied',
  [APPLIED]: 'Applied',
  [SHORTLISTED]: 'Applied',
  [AI_INTERVIEW]: 'AI Interview',
  [NOT_SHORTLISTED]: 'Needs Review',
  [INPERSON_INTERVIEW]: 'Interview',
  // "OFFERED": "Offered",
  [HIRED]: 'Hired',
  [REJECTED]: 'Rejeced',
};

// if status = NULL, Candidate Added
const SCREENING_STATUS_UI_MAPPING = {
  [ANALYZING]: 'Candidate Added',
  [AI_INTERVIEW_CREATED]: 'AI Interview Created',
  [AI_INTERVIEW_LINK_SENT]: 'AI Interview Sent',
  [SCREENING_ERROR]: 'Uncategorized',
  [SCREENING_COMPLETED]: 'Screening Completed',
  [AI_INTERVIEW_IN_PROGRESS]: 'Interview in-progress',
  [AI_INTERVIEW_EVALUATED]: 'Interview Completed',
  [IN_PERSON_INTERVIEW_SCHEDULED]: 'Interview',
  [CODING_INTERVIEW_SUBMITTED]: 'Coding Interview Submitted',
  [CANDIDATE_REJECTED]: 'Rejected',
  [CANDIDATE_HIRED]: 'Hired',
  [SCREENING_QUALIFIED]: 'Qualified',
  [SCREENING_DISQUALIFIED]: 'Disqualified',
};

const AI_RECOMMENDATION_STATUS_UI_MAPPINGS = {
  [NOT_ELIGIBLE_FOR_INTERVIEW]: 'Disqualified',
  [ELIGIBLE_FOR_INTERVIEW]: 'Qualified',
  [CANDIDATE_MATCHING]: 'Qualified',
  [CANDIDATE_BEST_MATCHING]: 'Best Match',
  [CANDIDATE_CLOSE_TO_MATCH]: 'Qualified',
  [REJECTED]: 'Disqualified',
};

const rejectHandler = (orgId, jobId, userId, saveChanges) => {
  // console.log(orgId, jobId, userId)
  rejectCandidate(orgId, jobId, userId)
    .then(res => {
      console.log(res);
      saveChanges();
    })
    .catch(err => {
      console.log(err);
    });
};

const hireHandler = (orgId, jobId, userId, saveChanges) => {
  hireCandidate(orgId, jobId, userId)
    .then(res => {
      console.log(res);
      saveChanges();
    })
    .catch(err => {
      console.log(err);
    });
};

const shortListHandler = (jobId, userId, saveChanges) => {
  const payload = {
    user_list: [userId],
  };
  shortlistCandidates(jobId, payload)
    .then(res => {
      console.log(res);
      saveChanges();
    })
    .catch(err => {
      console.log(err);
    });
};

const useGetCta = (jobId, setNavGroup) => {
  const theme = useTheme();
  const orgId = getOrganizationId(useAuthContext()?.user);
  const candidateDetails = useContext(CandidateProfileContext)?.obj;
  const saveChanges = useContext(CandidateProfileContext)?.saveChanges;
  if (!candidateDetails) return null;
  let interviewUrl = null;
  if (candidateDetails?.interview_details?.interview_url) {
    interviewUrl =
      process.env.REACT_APP_INTERVIEW_CLIENT_URL +
      candidateDetails.interview_details.interview_url;
  }
  const userId = candidateDetails?.candidate_info?.user_id;

  const jobCandidateStage = candidateDetails?.job_candidate_stage;
  const jobCandidateStatus = candidateDetails?.screening_status;
  // const jobCandidateStage = AI_INTERVIEW;
  // const jobCandidateStatus = AI_INTERVIEW_EVALUATED;

  if (
    jobCandidateStage === AI_INTERVIEW &&
    [AI_INTERVIEW_CREATED, AI_INTERVIEW_LINK_SENT].includes(jobCandidateStatus)
  ) {
    // Copy AI Interview Link
    return [
      {
        content: 'Copy AI Interview Link',
        icon: <Iconify color="white" icon="tabler:link" />,
        func: () => {
          if (interviewUrl) {
            navigator.clipboard.writeText(interviewUrl).then(
              () => {
                enqueueSnackbar('URL copied to clipboard');
              },
              () => {
                enqueueSnackbar({
                  message: 'Failed to copy URL',
                  variant: 'error',
                });
              },
            );
          }
        },
      },
    ];
  }
  if (
    [SOURCED, APPLIED, NOT_SHORTLISTED].includes(jobCandidateStage) &&
    [SCREENING_COMPLETED, SCREENING_ERROR, SCREENING_DISQUALIFIED].includes(
      jobCandidateStatus,
    )
  ) {
    // Reject/Shortlist
    return [
      {
        content: 'Reject',
        icon: (
          <Iconify
            color={theme.palette.background.primaryRed1}
            icon="tabler:x"
          />
        ),
        func: () => rejectHandler(orgId, jobId, userId, saveChanges),
      },
      {
        content: 'Shortlist',
        icon: <SvgColor src="/assets/tick.svg" />,
        func: () => {
          shortListHandler(jobId, userId, saveChanges);
        }, // createInterview link shortlist api in listing
      },
    ];
  }
  if (
    jobCandidateStage === AI_INTERVIEW &&
    jobCandidateStatus === AI_INTERVIEW_EVALUATED
  ) {
    // Reject/Schedule Interview
    return [
      {
        content: 'Reject',
        icon: (
          <Iconify
            color={theme.palette.background.primaryRed1}
            icon="tabler:x"
          />
        ),
        func: () => rejectHandler(orgId, jobId, userId, saveChanges),
      },
      {
        content: 'Schedule Interview',
        icon: <SvgColor src="/assets/users.svg" />,
        func: () => {
          setNavGroup(InPersonState);
        }, // tab change to inperson
      },
    ];
  }
  if (
    jobCandidateStage === INPERSON_INTERVIEW &&
    jobCandidateStatus === IN_PERSON_INTERVIEW_SCHEDULED
  ) {
    // Reject/Hire
    return [
      {
        content: 'Reject',
        icon: (
          <Iconify
            color={theme.palette.background.primaryRed1}
            icon="tabler:x"
          />
        ),
        func: () => rejectHandler(orgId, jobId, userId, saveChanges),
      },
      {
        content: 'Hire',
        icon: <SvgColor src="/assets/tick.svg" />,
        func: () => {
          hireHandler(orgId, jobId, userId, saveChanges);
        },
      },
    ];
  }
  if (jobCandidateStage === HIRED && jobCandidateStatus === CANDIDATE_HIRED) {
    // Hired (but not clickable)
    return [
      {
        content: 'Hired',
        icon: <SvgColor src="/assets/tick.svg" />,
        func: () => {},
      },
    ];
  }
  if (
    jobCandidateStage === REJECTED &&
    jobCandidateStatus === CANDIDATE_REJECTED
  ) {
    // Rejected
    return [
      {
        content: 'Rejected',
        icon: <Iconify color="white" icon="tabler:x" />,
        func: () => {},
      },
    ];
  }
  if ([SOURCED, APPLIED, NOT_SHORTLISTED].includes(jobCandidateStage)) {
    // Reject/Shortlist
    return [
      {
        content: 'Reject',
        icon: (
          <Iconify
            color={theme.palette.background.primaryRed1}
            icon="tabler:x"
          />
        ),
        func: () => rejectHandler(orgId, jobId, userId, saveChanges),
      },
      {
        content: 'Shortlist',
        icon: <Image src="/assets/tick.svg" />,
        func: () => {
          shortListHandler(jobId, userId, saveChanges);
        }, // createInterview link shortlist api in listing
      },
    ];
  }
};

const CandidateSocial = ({ jobId, setNavGroup }) => {
  // hooks
  const classes = useStyles();
  const shareProfilePopover = usePopover();
  const CTA = useGetCta(jobId, setNavGroup);
  const { obj: candidateDetails, candidateType } = useContext(
    CandidateProfileContext,
  );
  const { copyToClipboard } = useCopyToClipboard();
  const mdUp = useResponsive('up', 'md');

  // states
  const [profileJson, setProfileJson] = useState(null);
  const [isInternalLinkCopied, setIsInternalLinkCopied] = useState(false);
  const [isPublicLinkCopied, setIsPublicLinkCopied] = useState(false);
  const [isGeneratingPublicLink, setIsGeneratingPublicLink] = useState(false);
  const [publicProfileLink, setPublicProfileLink] = useState('');
  const [openEmailTooltip, setOpenEmailTooltip] = useState(false);
  const [openPhoneTooltip, setOpenPhoneTooltip] = useState(false);

  // variables
  let currentCompany = null;
  const candidateInfo = candidateDetails?.candidate_info;

  if (candidateDetails?.candidate_info?.current_role)
    currentCompany = candidateDetails?.candidate_info?.current_role;

  if (candidateDetails?.candidate_info?.current_org) {
    if (currentCompany)
      currentCompany += ` @${candidateDetails?.candidate_info?.current_org}`;
    else currentCompany = candidateDetails?.candidate_info?.current_org;
  }

  let AiRecommendation = null;

  if (candidateDetails?.ai_recommendation)
    AiRecommendation =
      AI_RECOMMENDATION_STATUS_UI_MAPPINGS[candidateDetails?.ai_recommendation];

  // functions
  const formatUrl = url => {
    if (!url) return null;
    if (url.startsWith('https://') || url.startsWith('http://')) {
      return url;
    }
    return `https://${url}`;
  };

  const handleGeneratePublicLink = async () => {
    try {
      setIsGeneratingPublicLink(true);

      const response = await getPublicSharingLink(
        jobId,
        candidateDetails?.candidate_info?.user_id,
      );

      if (response.success) {
        setPublicProfileLink(response.body.links.CANDIDATE_PUBLIC_PROFILE);
        enqueueSnackbar('Public link generated successfully');
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
    } finally {
      setIsGeneratingPublicLink(false);
    }
  };

  const handleCopyPublicLink = () => {
    copyToClipboard({
      text: BASE_URL + publicProfileLink,
      onSuccess: () => {
        setIsPublicLinkCopied(true);
        setTimeout(() => {
          setIsPublicLinkCopied(false);
        }, 2000);
      },
    });
  };

  const handleCopyInternalLink = () => {
    const link = `${window.location.href}${
      window.location.href.includes('?') ? '&' : '?'
    }show-candidate-profile=${candidateDetails?.candidate_info?.user_id}`;

    copyToClipboard({
      text: link,
      onSuccess: () => {
        setIsInternalLinkCopied(true);
        setTimeout(() => {
          setIsInternalLinkCopied(false);
        }, 2000);
      },
    });
  };

  // effects
  useEffect(() => {
    if (candidateDetails?.candidate_info?.profile_json) {
      try {
        const dumProfile = JSON.parse(
          candidateDetails?.candidate_info?.profile_json,
        );
        setProfileJson(dumProfile);
      } catch (error) {
        console.log('error occured');
      }
    }
    if (candidateDetails && candidateDetails?.public_profile_link) {
      setPublicProfileLink(candidateDetails?.public_profile_link);
    }
  }, [candidateDetails]);

  const user = {
    userImg: '',
    name: candidateInfo?.name,
    email: candidateInfo?.email,
    mobileNo: candidateInfo?.phone_number,
    linkedin: formatUrl(profileJson?.user_links?.linkedin),
    github: formatUrl(profileJson?.user_links?.github),
  };

  const showDetails = Object.keys(user).some(key => user[key]);

  return (
    showDetails && (
      <Stack
        className={classes.component}
        direction="column"
        position="relative"
      >
        {candidateType === 'JOB_CANDIDATE' && (
          <Stack className={classes.airecommendationDiv}>
            <SvgColor
              style={{ marginTop: -4 }}
              src="/assets/AI-recommendation.svg"
            />
            <Typography variant="input">{AiRecommendation}</Typography>
          </Stack>
        )}
        <Stack className={classes.componentSocialMain}>
          <Stack
            sx={{
              gap: {
                xs: '8px',
                md: '16px',
              },
            }}
          >
            {(user?.name || user?.email) && (
              <Avatar
                sx={{
                  alignSelf: 'center',
                  width: {
                    xs: '60px',
                    md: '72px',
                  },
                  height: {
                    xs: '60px',
                    md: '72px',
                  },
                  background: theme => theme.palette.background.primaryYellow1,
                }}
              >
                {user?.name?.charAt(0).toUpperCase() ||
                  user?.email?.charAt(0).toUpperCase()}
              </Avatar>
            )}
            <Stack
              sx={{
                gap: {
                  xs: '4px',
                  md: '8px',
                },
              }}
            >
              {(user?.name || user?.email) && (
                <Typography
                  variant={mdUp ? 'h4' : 'subheading2'}
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  {user?.name || user?.email}
                </Typography>
              )}
              {currentCompany && (
                <Typography
                  variant={mdUp ? 'input' : 'caption'}
                  className={classes.currentJob}
                >
                  {currentCompany}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" className={classes.socialIconsDiv}>
            {/* {user?.email && (
              <ClickAwayListener onClickAway={() => setOpenEmailTooltip(false)}>
                <div>
                  <Tooltip
                    title={
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={{ md: '8px', xs: '4px' }}
                      >
                        <Typography
                          variant="caption"
                          width="90%"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: '150px', md: '200px' },
                          }}
                        >
                          {user?.email}
                        </Typography>
                        <Stack
                          width="10%"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Iconify
                            sx={{ cursor: 'pointer' }}
                            icon="tabler:copy"
                            width={16}
                            onClick={() => {
                              navigator.clipboard.writeText(user?.email).then(
                                () => {
                                  enqueueSnackbar('Email copied to clipboard');
                                },
                                () => {
                                  enqueueSnackbar({
                                    message: 'Failed to copy Email',
                                    variant: 'error',
                                  });
                                },
                              );
                            }}
                          />
                        </Stack>
                      </Stack>
                    }
                    PopperProps={{
                      disablePortal: !mdUp,
                    }}
                    disableFocusListener={!mdUp}
                    disableHoverListener={!mdUp}
                    disableTouchListener={!mdUp}
                    open={openEmailTooltip}
                    onOpen={() => setOpenEmailTooltip(true)}
                    onClose={() => setOpenEmailTooltip(false)}
                  >
                    <Stack
                      onClick={() => setOpenEmailTooltip(true)}
                      className={classes.iconPadDiv}
                    >
                      <SvgColor src="/assets/email.svg" />
                    </Stack>
                  </Tooltip>
                </div>
              </ClickAwayListener>
            )}
            {user?.mobileNo && (
              <ClickAwayListener onClickAway={() => setOpenPhoneTooltip(false)}>
                <div>
                  <Tooltip
                    title={
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={{ md: '8px', xs: '4px' }}
                      >
                        <Typography
                          variant="caption"
                          width="90%"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: '150px', md: '200px' },
                          }}
                        >
                          {user?.mobileNo}
                        </Typography>
                        <Stack
                          width="10%"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Iconify
                            width={16}
                            sx={{ cursor: 'pointer' }}
                            icon="tabler:copy"
                            onClick={() => {
                              navigator.clipboard
                                .writeText(user?.mobileNo)
                                .then(
                                  () => {
                                    enqueueSnackbar(
                                      'Phone number copied to clipboard',
                                    );
                                  },
                                  () => {
                                    enqueueSnackbar({
                                      message: 'Failed to copy Phone number',
                                      variant: 'error',
                                    });
                                  },
                                );
                            }}
                          />
                        </Stack>
                      </Stack>
                    }
                    open={openPhoneTooltip}
                    onOpen={() => setOpenPhoneTooltip(true)}
                    onClose={() => setOpenPhoneTooltip(false)}
                  >
                    <Stack
                      onClick={() => setOpenPhoneTooltip(true)}
                      className={classes.iconPadDiv}
                    >
                      <SvgColor src="/assets/phone.svg" />
                    </Stack>
                  </Tooltip>
                </div>
              </ClickAwayListener>
            )}
            {user?.linkedin && (
              <Link to={user?.linkedin} target="_blank">
                <Stack className={classes.iconPadDiv}>
                  <SvgColor src="/assets/linkedin.svg" />
                </Stack>
              </Link>
            )}
            {user?.github && (
              <Link to={user?.github} target="_blank">
                <Stack className={classes.iconPadDiv}>
                  <SvgColor src="/assets/github.svg" />
                </Stack>
              </Link>
            )} */}
            {candidateType === 'JOB_CANDIDATE' && (
              <>
                <Stack
                  aria-haspopup="true"
                  aria-owns={
                    shareProfilePopover.open
                      ? 'share-profile-popover'
                      : undefined
                  }
                  sx={{
                    cursor: 'pointer',
                  }}
                  className={classes.iconPadDiv}
                  onClick={shareProfilePopover.onOpen}
                >
                  <Iconify icon="tabler:share" />
                </Stack>
                <CustomPopover
                  id="share-profile-popover"
                  sx={{
                    bgcolor: 'background.primaryBlack1',
                    borderColor: 'border.primaryBlack1',
                    borderRadius: '12px',
                  }}
                  open={shareProfilePopover.open}
                  onClose={shareProfilePopover.onClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  disableRestoreFocus
                >
                  <Stack
                    direction="row"
                    px="8px"
                    alignItems="center"
                    justifyContent="center"
                    spacing="4px"
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleCopyInternalLink()}
                      sx={{
                        width: '200px',
                        bgcolor: isInternalLinkCopied
                          ? 'background.primaryGreen2'
                          : 'background.primaryWhite1',
                        color: isInternalLinkCopied
                          ? 'content.primaryGreen1'
                          : 'content.primaryBlack1',

                        '&:hover': {
                          color: isInternalLinkCopied
                            ? 'background.primaryGreen2'
                            : 'content.primaryBlack1',
                          bgcolor: isInternalLinkCopied
                            ? 'content.primaryGreen1'
                            : 'background.primaryGrey2',
                        },
                      }}
                      endIcon={
                        isInternalLinkCopied ? null : (
                          <Iconify icon="tabler:copy" />
                        )
                      }
                    >
                      {isInternalLinkCopied ? 'Copied' : 'Internal Link'}
                    </Button>
                    <LoadingButton
                      variant="outlined"
                      sx={{
                        width: '200px',
                        color: 'content.primaryWhite',
                        '&:hover': {
                          borderColor: 'border.primaryAccent1',
                          bgcolor: 'background.primaryAccent1',
                        },
                      }}
                      loading={isGeneratingPublicLink}
                      loadingPosition="start"
                      onClick={
                        publicProfileLink
                          ? () => handleCopyPublicLink()
                          : () => handleGeneratePublicLink()
                      }
                      endIcon={
                        publicProfileLink &&
                        !isPublicLinkCopied && <Iconify icon="tabler:copy" />
                      }
                    >
                      {(() => {
                        if (publicProfileLink) {
                          return isPublicLinkCopied ? 'Copied' : 'Public Link';
                        }
                        return 'Generate Public Link';
                      })()}
                    </LoadingButton>
                  </Stack>
                </CustomPopover>
              </>
            )}
          </Stack>
          {candidateType === 'CANDIDATE' && (
            <Button
              variant="contained"
              onClick={() => handleCopyInternalLink()}
              startIcon={<Iconify icon="tabler:share" />}
            >
              Copy profile link
            </Button>
          )}
          {candidateType === 'JOB_CANDIDATE' && (
            <Stack>
              {CTA &&
                (CTA.length === 2 ? (
                  <Stack direction="row" spacing="8px">
                    <Button
                      variant="contained"
                      size="large"
                      className={classes.errButton}
                      startIcon={CTA[0]?.icon}
                      onClick={() => {
                        CTA[0]?.func();
                      }}
                    >
                      {CTA[0]?.content}
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      className={classes.button}
                      startIcon={CTA[1]?.icon}
                      onClick={() => {
                        CTA[1]?.func();
                      }}
                    >
                      {CTA[1]?.content}
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    className={classes.button}
                    startIcon={CTA[0]?.icon}
                    onClick={() => {
                      CTA[0]?.func();
                    }}
                    disabled={['Hired', 'Rejected'].includes(CTA[0]?.content)}
                  >
                    {CTA[0]?.content}
                  </Button>
                ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    )
  );
};

const CandidateDetails = () => {
  const classes = useStyles();
  const { obj: candidateDetails, candidateType } = useContext(
    CandidateProfileContext,
  );

  const user = {
    ranking: candidateDetails?.interview_details?.rank,
    aiInterviewScore: candidateDetails?.interview_details?.total_score,
    stage: candidateDetails?.job_candidate_stage,
    status: candidateDetails?.screening_status,
    source: candidateDetails?.candidate_info?.candidate_source,
    addedDate: candidateDetails?.created_at,
    lastAppliedJobTitle:
      candidateDetails?.candidate_info?.last_applied_job_title,
    lastAppliedJobStatus:
      candidateDetails?.candidate_info?.last_applied_job_status,
    candidateAddedDate: candidateDetails?.candidate_info?.created_at,
  };

  const showDetails = Object.keys(user).some(key => user[key]);

  return (
    showDetails && (
      <>
        <Stack className={classes.component}>
          <Stack className={classes.candidateDetailsDiv}>
            <Typography
              variant="paragraph"
              className={classes.candidateDetailsAndTrackingText}
            >
              Candidate Details
            </Typography>
            <Stack className={classes.detailsContentDiv}>
              {candidateType === 'CANDIDATE' && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">Job Applied</Typography>
                  <Tooltip title={user?.lastAppliedJobTitle || '-'}>
                    <Typography
                      variant="input"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '130px',
                      }}
                    >
                      {user?.lastAppliedJobTitle || '-'}
                    </Typography>
                  </Tooltip>
                </Stack>
              )}
              {candidateType === 'CANDIDATE' && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">Last Status</Typography>
                  <Typography variant="input">
                    {user?.lastAppliedJobStatus
                      ? JOB_CANDIDATE_STAGE_UI_MAPPING[
                          user?.lastAppliedJobStatus
                        ]
                      : 'Candidate Added'}
                  </Typography>
                </Stack>
              )}
              {candidateType === 'JOB_CANDIDATE' && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">Ranking</Typography>
                  <Typography variant="input">
                    {user?.ranking ? user?.ranking : '-'}
                  </Typography>
                </Stack>
              )}
              {candidateType === 'JOB_CANDIDATE' && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">AI Interview Score</Typography>
                  <Typography variant="input">
                    {user?.aiInterviewScore !== null
                      ? user?.aiInterviewScore
                      : '-'}
                  </Typography>
                </Stack>
              )}
              {user?.stage && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">Stage</Typography>
                  <Typography variant="input">
                    {user?.stage
                      ? JOB_CANDIDATE_STAGE_UI_MAPPING[user?.stage]
                      : '-'}
                  </Typography>
                </Stack>
              )}
              {candidateType === 'JOB_CANDIDATE' && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">Status</Typography>
                  <Typography variant="input">
                    {user?.status
                      ? SCREENING_STATUS_UI_MAPPING[user?.status]
                      : 'Candidate Added'}
                  </Typography>
                </Stack>
              )}
              {user?.source && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">Source</Typography>
                  <Typography variant="input">
                    {user?.source ? user?.source : '-'}
                  </Typography>
                </Stack>
              )}
              {candidateType === 'JOB_CANDIDATE' && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">Candidate Added Date</Typography>
                  <Typography variant="input">
                    {user?.addedDate
                      ? formatFullDateString(user?.addedDate)
                      : '-'}
                  </Typography>
                </Stack>
              )}
              {candidateType === 'CANDIDATE' && (
                <Stack direction="row" className={classes.detailsContent}>
                  <Typography variant="input">Candidate Added Date</Typography>
                  <Typography variant="input">
                    {user?.candidateAddedDate
                      ? formatFullDateString(user?.candidateAddedDate)
                      : '-'}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Divider />
      </>
    )
  );
};

const CandidateTracking = () => {
  const classes = useStyles();
  const candidateDetails = useContext(CandidateProfileContext)?.obj;

  return (
    <Stack className={classes.component} sx={{ overflowY: 'auto' }}>
      {candidateDetails && candidateDetails?.candidate_timeline && (
        <Stack>
          <Typography
            variant="paragraph"
            className={classes.candidateDetailsAndTrackingText}
          >
            Tracking
          </Typography>
          <TrackingTimeline
            data={[...candidateDetails.candidate_timeline].reverse()}
          />
        </Stack>
      )}
    </Stack>
  );
};

const LeftContainer = ({ jobId, setNavGroup }) => {
  const classes = useStyles();

  return (
    <Stack className={classes.mainDiv}>
      <Stack height="100%" gap="1.2em">
        <CandidateSocial jobId={jobId} setNavGroup={setNavGroup} />
      </Stack>
    </Stack>
  );
};

export default LeftContainer;

LeftContainer.propTypes = {
  jobId: PropTypes.string,
  setNavGroup: PropTypes.func.isRequired,
};

CandidateSocial.propTypes = {
  jobId: PropTypes.string.isRequired,
  setNavGroup: PropTypes.func.isRequired,
};

CandidateDetails.propTypes = {};
